import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/providers/auth-provider'
import { PageLoader } from '@/components/shared/loading-spinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
  requireApproval?: boolean
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireApproval = true,
}: ProtectedRouteProps) {
  const { user, profile, isLoading, isStaff, isApproved } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <PageLoader />
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // If auth resolved but profile is missing (fetch failed, DB issue, etc.),
  // redirect to login instead of spinning forever
  if (!profile) {
    return <Navigate to="/auth/login" replace />
  }

  if (requireAdmin && !isStaff) {
    return <Navigate to="/dashboard" replace />
  }

  if (requireApproval && !isApproved && !isStaff) {
    return <Navigate to="/pending-approval" replace />
  }

  // Redirect to onboarding if not completed (skip for staff and the onboarding page itself)
  if (
    requireApproval &&
    !isStaff &&
    profile.onboarding_complete === false &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}
